import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, MapPin, Briefcase, Building2, Banknote, Bookmark, BookmarkX } from "lucide-react";
import { toast } from "sonner";

const SavedJobs = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  const load = () => {
    if (user) api.get("/saved-jobs").then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const remove = async (job) => {
    try {
      await api.delete(`/saved-jobs/${job.id}`);
      setJobs((js) => js.filter((j) => j.id !== job.id));
      toast("Removed from saved");
    } catch {
      toast.error("Could not remove");
    }
  };

  if (authLoading || (!user && loading))
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  return (
    <PageShell>
      <div className="mx-auto max-w-[1000px] px-6 py-10 lg:px-8">
        <div className="flex items-center gap-2">
          <Bookmark className="h-6 w-6 text-[#16A34A]" />
          <h1 className="font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Saved Jobs</h1>
        </div>
        <p className="mt-2 text-[16px] text-slate-600">Opportunities you've bookmarked to revisit later.</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div>
        ) : jobs.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
            <p className="text-slate-500">You haven't saved any jobs yet.</p>
            <Button onClick={() => navigate("/opportunities")} className="mt-4 h-11 rounded-xl bg-[#16A34A] px-6 font-semibold text-white hover:bg-[#138a3f]">Browse Opportunities</Button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            {jobs.map((job) => (
              <div key={job.id} className="flex flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-[20px] font-bold text-[#0B2545]">{job.role_title}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600"><Building2 className="h-4 w-4 text-slate-400" />{job.organisation_name}</p>
                  </div>
                  <span className="rounded-full bg-[#1E4FA3]/10 px-3 py-1 text-xs font-semibold text-[#1E4FA3]">{job.qualification}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{job.location}</span>
                  <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-slate-400" />{job.employment_type}</span>
                  {job.salary_range && <span className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-slate-400" />{job.salary_range}</span>}
                </div>
                <p className="mt-3 line-clamp-2 text-[15px] leading-relaxed text-slate-600">{job.description}</p>
                <div className="mt-5 flex items-center justify-end">
                  <Button variant="outline" onClick={() => remove(job)} className="h-10 rounded-lg border-2 border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600">
                    <BookmarkX className="mr-1.5 h-4 w-4" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-8 text-sm text-slate-500">
          Looking for more? <Link to="/opportunities" className="font-semibold text-[#1E4FA3] hover:underline">Browse all opportunities</Link>
        </p>
      </div>
    </PageShell>
  );
};

export default SavedJobs;
